import {AfterViewInit, Component, computed, effect, ElementRef, inject, input, viewChild} from "@angular/core"
import {
    BdApi,
    BdApiWebcomponent,
    ConfiguratorState,
    isColorWithSubstrate,
    isMaterial,
    isQuiltedMaterial,
    NumberValue,
    UsedElements,
} from "@app/common/components/viewers/configurator/extensions/bd-cloud/types"
import {NotificationsService} from "@app/common/services/notifications/notifications.service"
import {TestCaseEntry, updateIframe} from "@app/test/manual-testing/helpers/configurator-test-helpers"
import {getBdCloudPage} from "@app/test/manual-testing/helpers/bdcloud-test-pages"
import {TestContainerComponent} from "@app/test/manual-testing/test-container/test-container.component"
import {configuratorWcModuleUrls, Source} from "@app/test/manual-testing/types/types"
import {CM_LOGGER_APP} from "@app/common/services/logging"
import {CMConfiguratorElement} from "@app/common/components/viewers/configurator/types/cm-configurator"

//Default mattress accessories, see frontend/bd-cloud/src/utils/default-accessories.ts. Zipper and puller must share the same size (M5).
const zipperTemplateId = "3419c2ae-a003-4781-aab6-db0344b2c7f6"
const pullerTemplateId = "a30a4c71-9680-4b4b-868d-a222b46a646c"

@Component({
    selector: "cm-bdcloud-test",
    templateUrl: "./bdcloud-test.component.html",
    styleUrls: ["./bdcloud-test.component.scss"],
    imports: [TestContainerComponent],
})
export class BdcloudTestComponent implements AfterViewInit {
    private readonly notifications = inject(NotificationsService)
    readonly $source = input.required<Source>({alias: "source"})
    private readonly logger = inject(CM_LOGGER_APP)

    private readonly $iframe = viewChild.required<ElementRef<HTMLIFrameElement>>("configuratorIframe")

    private readonly bdCloudTemplateUuid = "ac7e6564-0304-4eab-9486-400a514a5b4e"

    private savedCameraParams: string | undefined

    readonly $moduleUrl = computed(() => configuratorWcModuleUrls[this.$source()])

    testCases: TestCaseEntry[] = [
        {
            type: "info",
            name: "What you should see",
            tooltip: "A mattress model, check if elements can be selected.",
            fct: () => {},
        },
        {
            type: "test",
            name: "Load Cover",
            tooltip: "A mattress model using different assets",
            fct: () => {
                const iframe = this.$iframe().nativeElement
                updateIframe(iframe, getBdCloudPage(this.$moduleUrl(), "ac7e6564-0304-4eab-9486-400a514a5b4e"))
            },
        },
        {
            type: "test",
            name: "Expose Assets",
            tooltip: "Fetches used elements and compares against expected result",
            fct: async () => {
                const bdApi = this.getBdApi()
                const actual = await bdApi.getUsedElements()
                const expected: UsedElements = {
                    assets: [
                        {type: "Fabric", colormassId: "f09ec9f6-1856-4479-9af3-ec36e8b7f437", bdId: "40079819"},
                        {type: "Fabric", colormassId: "34d144c2-3295-4a29-9072-69768b5a17c5"},
                        {type: "Fabric", colormassId: "da6affab-1025-42f5-8db5-bff915e6352a", bdId: "40235303"},
                        {type: "Quilt", colormassId: "c36fd292-8b86-494b-8cd5-d2a6763fb601"},
                        // {type: "Puller", colormassId: "e57ed091-f191-43a1-8a34-347b03f999ca"},
                        // {type: "Zipper", colormassId: "c9fa27f5-0bee-4890-afbc-8f4d07eab50d"},
                        // {type: "Tape", colormassId: "7afccd2f-07b9-4f9f-a361-7d1c740ca6b7"},
                        {type: "Piping", colormassId: "c97f5318-cb62-46e4-9525-ffbfe8f2412c"},
                    ],
                    colors: ["#d42525"],
                    errors: [],
                }
                this.compareUsedElements(actual, expected)
            },
        },
        {
            type: "test",
            name: "Change Dimension",
            tooltip: "Changes the first dimension value, then verifies it was updated",
            fct: async () => {
                const testName = "Change Dimension"
                const bdApi = this.getBdApi()
                const dimensions = await this.getCurrentDimensions(bdApi)
                const target = dimensions.find((d) => d.name.toLowerCase().includes("width"))
                if (!target) {
                    this.notifications.showError(`${testName}: FAIL — no 'width' dimension found`)
                    return
                }
                const newValue = 200
                bdApi.setNumberInput(target.inputId, newValue)
                try {
                    await this.waitForState(bdApi, (s) => {
                        const updated = s.dimensions?.find((d) => d.inputId === target.inputId)
                        return !!updated && updated.value === newValue
                    })
                    this.notifications.showInfo(`${testName}: PASS — ${target.name} is now ${newValue}`)
                } catch {
                    this.notifications.showError(`${testName}: FAIL — ${target.name} did not become ${newValue}`)
                }
            },
        },
        {
            type: "test",
            name: "Undo Dimension Change",
            tooltip: "Undoes the previous dimension change. Run 'Change Dimension' first.",
            fct: async () => {
                const testName = "Undo Dimension Change"
                const bdApi = this.getBdApi()
                const dimensionsBefore = await this.getCurrentDimensions(bdApi)
                if (!bdApi.canUndo()) {
                    this.notifications.showError(`${testName}: FAIL — nothing to undo. Run 'Change Dimension' first.`)
                    return
                }
                bdApi.undo()
                try {
                    await this.waitForState(bdApi, (s) => {
                        if (!s.dimensions || dimensionsBefore.length === 0) return false
                        const target = dimensionsBefore[0]
                        const updated = s.dimensions.find((d) => d.inputId === target.inputId)
                        return !!updated && updated.value !== target.value
                    })
                    this.notifications.showInfo(`${testName}: PASS — dimension value reverted`)
                } catch {
                    this.notifications.showError(`${testName}: FAIL — dimension did not change after undo`)
                }
            },
        },
        {
            type: "test",
            name: "Redo Dimension Change",
            tooltip: "Redoes the previously undone dimension change. Run 'Undo Dimension Change' first.",
            fct: async () => {
                const testName = "Redo Dimension Change"
                const bdApi = this.getBdApi()
                const dimensionsBefore = await this.getCurrentDimensions(bdApi)
                if (!bdApi.canRedo()) {
                    this.notifications.showError(`${testName}: FAIL — nothing to redo. Run 'Undo Dimension Change' first.`)
                    return
                }
                bdApi.redo()
                try {
                    await this.waitForState(bdApi, (s) => {
                        if (!s.dimensions || dimensionsBefore.length === 0) return false
                        const target = dimensionsBefore[0]
                        const updated = s.dimensions.find((d) => d.inputId === target.inputId)
                        return !!updated && updated.value !== target.value
                    })
                    this.notifications.showInfo(`${testName}: PASS — dimension value reapplied`)
                } catch {
                    this.notifications.showError(`${testName}: FAIL — dimension did not change after redo`)
                }
            },
        },
        {
            type: "test",
            name: "Set Material",
            tooltip: "Sets a material on the selected fabric element. Click a fabric element first.",
            fct: async () => {
                const testName = "Set Material"
                const bdApi = this.getBdApi()
                const state = await this.getCurrentState(bdApi)
                const selected = state.selectedElement
                if (!selected || selected.type !== "FabricLayer") {
                    this.notifications.showError(`${testName}: FAIL — no FabricLayer selected`)
                    return
                }
                const inputId = selected.materialInput.id
                const materialArticleId = "40235170"
                await bdApi.setMaterial(inputId, materialArticleId)
                try {
                    await this.waitForState(bdApi, (s) => {
                        const el = s.selectedElement
                        return (
                            !!el &&
                            el.type === "FabricLayer" &&
                            !!el.materialInput.value &&
                            isMaterial(el.materialInput.value) &&
                            el.materialInput.value.materialArticleId === materialArticleId
                        )
                    })
                    this.notifications.showInfo(`${testName}: PASS — materialArticleId is now ${materialArticleId}`)
                } catch {
                    this.notifications.showError(`${testName}: FAIL — materialArticleId did not become ${materialArticleId}`)
                }
            },
        },
        {
            type: "test",
            name: "Set Material H-Offset",
            tooltip: "Sets horizontal offset on a plain fabric element. Click a fabric element first.",
            fct: async () => {
                const testName = "Set Material H-Offset"
                const bdApi = this.getBdApi()
                const state = await this.getCurrentState(bdApi)
                const selected = state.selectedElement
                if (!selected || selected.type !== "FabricLayer") {
                    this.notifications.showError(`${testName}: FAIL — no FabricLayer selected`)
                    return
                }
                const inputId = selected.materialInput.id
                const offset = 10
                bdApi.setMaterialHorizontalOffset(inputId, offset)
                try {
                    await this.waitForState(bdApi, (s) => {
                        const el = s.selectedElement
                        return (
                            !!el &&
                            el.type === "FabricLayer" &&
                            !!el.materialInput.value &&
                            isMaterial(el.materialInput.value) &&
                            el.materialInput.value.horizontalOffset === offset
                        )
                    })
                    this.notifications.showInfo(`${testName}: PASS — horizontalOffset is now ${offset}`)
                } catch {
                    this.notifications.showError(`${testName}: FAIL — horizontalOffset did not become ${offset}`)
                }
            },
        },
        {
            type: "test",
            name: "Set Material V-Offset",
            tooltip: "Sets vertical offset on a plain fabric element. Click a fabric element first.",
            fct: async () => {
                const testName = "Set Material V-Offset"
                const bdApi = this.getBdApi()
                const state = await this.getCurrentState(bdApi)
                const selected = state.selectedElement
                if (!selected || selected.type !== "FabricLayer") {
                    this.notifications.showError(`${testName}: FAIL — no FabricLayer selected`)
                    return
                }
                const inputId = selected.materialInput.id
                const offset = 10
                bdApi.setMaterialVerticalOffset(inputId, offset)
                try {
                    await this.waitForState(bdApi, (s) => {
                        const el = s.selectedElement
                        return (
                            !!el &&
                            el.type === "FabricLayer" &&
                            !!el.materialInput.value &&
                            isMaterial(el.materialInput.value) &&
                            el.materialInput.value.verticalOffset === offset
                        )
                    })
                    this.notifications.showInfo(`${testName}: PASS — verticalOffset is now ${offset}`)
                } catch {
                    this.notifications.showError(`${testName}: FAIL — verticalOffset did not become ${offset}`)
                }
            },
        },
        {
            type: "test",
            name: "Set Material Rotation",
            tooltip: "Sets rotation on a plain fabric element. Click a fabric element first.",
            fct: async () => {
                const testName = "Set Material Rotation"
                const bdApi = this.getBdApi()
                const state = await this.getCurrentState(bdApi)
                const selected = state.selectedElement
                if (!selected || selected.type !== "FabricLayer") {
                    this.notifications.showError(`${testName}: FAIL — no FabricLayer selected`)
                    return
                }
                const inputId = selected.materialInput.id
                const rotation = 45
                bdApi.setMaterialRotation(inputId, rotation)
                try {
                    await this.waitForState(bdApi, (s) => {
                        const el = s.selectedElement
                        return (
                            !!el &&
                            el.type === "FabricLayer" &&
                            !!el.materialInput.value &&
                            isMaterial(el.materialInput.value) &&
                            el.materialInput.value.rotation === rotation
                        )
                    })
                    this.notifications.showInfo(`${testName}: PASS — rotation is now ${rotation}`)
                } catch {
                    this.notifications.showError(`${testName}: FAIL — rotation did not become ${rotation}`)
                }
            },
        },
        {
            type: "test",
            name: "Set Quilt",
            tooltip: "Sets a quilt on the selected fabric element. Click a fabric element first.",
            fct: async () => {
                const testName = "Set Quilt"
                const bdApi = this.getBdApi()
                const state = await this.getCurrentState(bdApi)
                const selected = state.selectedElement
                if (!selected || selected.type !== "FabricLayer") {
                    this.notifications.showError(`${testName}: FAIL — no FabricLayer selected`)
                    return
                }
                const inputId = selected.materialInput.id
                const quiltId = "d826cc16-d1bd-4799-81ae-55c3dd12a356"
                await bdApi.setQuilt(inputId, quiltId)
                try {
                    await this.waitForState(bdApi, (s) => {
                        const el = s.selectedElement
                        return (
                            !!el &&
                            el.type === "FabricLayer" &&
                            !!el.materialInput.value &&
                            isQuiltedMaterial(el.materialInput.value) &&
                            el.materialInput.value.quiltId === quiltId
                        )
                    })
                    this.notifications.showInfo(`${testName}: PASS — quiltId is now ${quiltId}`)
                } catch {
                    this.notifications.showError(`${testName}: FAIL — quiltId did not become ${quiltId}`)
                }
            },
        },
        {
            type: "test",
            name: "Set Quilt Strength",
            tooltip: "Sets quilt strength on the selected fabric element. Run 'Set Quilt' first, then click the same element.",
            fct: async () => {
                const testName = "Set Quilt Strength"
                const bdApi = this.getBdApi()
                const state = await this.getCurrentState(bdApi)
                const selected = state.selectedElement
                if (!selected || selected.type !== "FabricLayer") {
                    this.notifications.showError(`${testName}: FAIL — no FabricLayer selected`)
                    return
                }
                const inputId = selected.materialInput.id
                const strength = 0.7
                bdApi.setQuiltStrength(inputId, strength)
                try {
                    await this.waitForState(bdApi, (s) => {
                        const el = s.selectedElement
                        return (
                            !!el &&
                            el.type === "FabricLayer" &&
                            !!el.materialInput.value &&
                            isQuiltedMaterial(el.materialInput.value) &&
                            el.materialInput.value.quiltStrength === strength
                        )
                    })
                    this.notifications.showInfo(`${testName}: PASS — quiltStrength is now ${strength}`)
                } catch {
                    this.notifications.showError(`${testName}: FAIL — quiltStrength did not become ${strength}`)
                }
            },
        },
        {
            type: "test",
            name: "Set Material H-Offset (Quilted)",
            tooltip: "Sets horizontal offset on a quilted fabric element. Run 'Set Quilt' first, then click the same element.",
            fct: async () => {
                const testName = "Set Material H-Offset (Quilted)"
                const bdApi = this.getBdApi()
                const state = await this.getCurrentState(bdApi)
                const selected = state.selectedElement
                if (!selected || selected.type !== "FabricLayer") {
                    this.notifications.showError(`${testName}: FAIL — no FabricLayer selected`)
                    return
                }
                const inputId = selected.materialInput.id
                const offset = 10
                bdApi.setMaterialHorizontalOffset(inputId, offset)
                try {
                    await this.waitForState(bdApi, (s) => {
                        const el = s.selectedElement
                        return (
                            !!el &&
                            el.type === "FabricLayer" &&
                            !!el.materialInput.value &&
                            isQuiltedMaterial(el.materialInput.value) &&
                            el.materialInput.value.horizontalOffset === offset
                        )
                    })
                    this.notifications.showInfo(`${testName}: PASS — horizontalOffset is now ${offset}`)
                } catch {
                    this.notifications.showError(`${testName}: FAIL — horizontalOffset did not become ${offset}`)
                }
            },
        },
        {
            type: "test",
            name: "Set Material V-Offset (Quilted)",
            tooltip: "Sets vertical offset on a quilted fabric element. Run 'Set Quilt' first, then click the same element.",
            fct: async () => {
                const testName = "Set Material V-Offset (Quilted)"
                const bdApi = this.getBdApi()
                const state = await this.getCurrentState(bdApi)
                const selected = state.selectedElement
                if (!selected || selected.type !== "FabricLayer") {
                    this.notifications.showError(`${testName}: FAIL — no FabricLayer selected`)
                    return
                }
                const inputId = selected.materialInput.id
                const offset = 10
                bdApi.setMaterialVerticalOffset(inputId, offset)
                try {
                    await this.waitForState(bdApi, (s) => {
                        const el = s.selectedElement
                        return (
                            !!el &&
                            el.type === "FabricLayer" &&
                            !!el.materialInput.value &&
                            isQuiltedMaterial(el.materialInput.value) &&
                            el.materialInput.value.verticalOffset === offset
                        )
                    })
                    this.notifications.showInfo(`${testName}: PASS — verticalOffset is now ${offset}`)
                } catch {
                    this.notifications.showError(`${testName}: FAIL — verticalOffset did not become ${offset}`)
                }
            },
        },
        {
            type: "test",
            name: "Set Material Rotation (Quilted)",
            tooltip: "Sets rotation on a quilted fabric element. Run 'Set Quilt' first, then click the same element.",
            fct: async () => {
                const testName = "Set Material Rotation (Quilted)"
                const bdApi = this.getBdApi()
                const state = await this.getCurrentState(bdApi)
                const selected = state.selectedElement
                if (!selected || selected.type !== "FabricLayer") {
                    this.notifications.showError(`${testName}: FAIL — no FabricLayer selected`)
                    return
                }
                const inputId = selected.materialInput.id
                const rotation = 45
                bdApi.setMaterialRotation(inputId, rotation)
                try {
                    await this.waitForState(bdApi, (s) => {
                        const el = s.selectedElement
                        return (
                            !!el &&
                            el.type === "FabricLayer" &&
                            !!el.materialInput.value &&
                            isQuiltedMaterial(el.materialInput.value) &&
                            el.materialInput.value.rotation === rotation
                        )
                    })
                    this.notifications.showInfo(`${testName}: PASS — rotation is now ${rotation}`)
                } catch {
                    this.notifications.showError(`${testName}: FAIL — rotation did not become ${rotation}`)
                }
            },
        },
        {
            type: "test",
            name: "Set Quilt H-Offset",
            tooltip: "Sets quilt horizontal offset. Run 'Set Quilt' first, then click the same element.",
            fct: async () => {
                const testName = "Set Quilt H-Offset"
                const bdApi = this.getBdApi()
                const state = await this.getCurrentState(bdApi)
                const selected = state.selectedElement
                if (!selected || selected.type !== "FabricLayer") {
                    this.notifications.showError(`${testName}: FAIL — no FabricLayer selected`)
                    return
                }
                const inputId = selected.materialInput.id
                const offset = 10
                bdApi.setQuiltHorizontalOffset(inputId, offset)
                try {
                    await this.waitForState(bdApi, (s) => {
                        const el = s.selectedElement
                        return (
                            !!el &&
                            el.type === "FabricLayer" &&
                            !!el.materialInput.value &&
                            isQuiltedMaterial(el.materialInput.value) &&
                            el.materialInput.value.quiltHorizontalOffset === offset
                        )
                    })
                    this.notifications.showInfo(`${testName}: PASS — quiltHorizontalOffset is now ${offset}`)
                } catch {
                    this.notifications.showError(`${testName}: FAIL — quiltHorizontalOffset did not become ${offset}`)
                }
            },
        },
        {
            type: "test",
            name: "Set Quilt V-Offset",
            tooltip: "Sets quilt vertical offset. Run 'Set Quilt' first, then click the same element.",
            fct: async () => {
                const testName = "Set Quilt V-Offset"
                const bdApi = this.getBdApi()
                const state = await this.getCurrentState(bdApi)
                const selected = state.selectedElement
                if (!selected || selected.type !== "FabricLayer") {
                    this.notifications.showError(`${testName}: FAIL — no FabricLayer selected`)
                    return
                }
                const inputId = selected.materialInput.id
                const offset = 10
                bdApi.setQuiltVerticalOffset(inputId, offset)
                try {
                    await this.waitForState(bdApi, (s) => {
                        const el = s.selectedElement
                        return (
                            !!el &&
                            el.type === "FabricLayer" &&
                            !!el.materialInput.value &&
                            isQuiltedMaterial(el.materialInput.value) &&
                            el.materialInput.value.quiltVerticalOffset === offset
                        )
                    })
                    this.notifications.showInfo(`${testName}: PASS — quiltVerticalOffset is now ${offset}`)
                } catch {
                    this.notifications.showError(`${testName}: FAIL — quiltVerticalOffset did not become ${offset}`)
                }
            },
        },
        {
            type: "test",
            name: "Remove Quilt",
            tooltip: "Removes the quilt from the selected fabric element. Run 'Set Quilt' first, then click the same element.",
            fct: async () => {
                const testName = "Remove Quilt"
                const bdApi = this.getBdApi()
                const state = await this.getCurrentState(bdApi)
                const selected = state.selectedElement
                if (!selected || selected.type !== "FabricLayer") {
                    this.notifications.showError(`${testName}: FAIL — no FabricLayer selected`)
                    return
                }
                const inputId = selected.materialInput.id
                await bdApi.setQuilt(inputId, undefined)
                try {
                    await this.waitForState(bdApi, (s) => {
                        const el = s.selectedElement
                        return (
                            !!el &&
                            el.type === "FabricLayer" &&
                            !!el.materialInput.value &&
                            isQuiltedMaterial(el.materialInput.value) &&
                            el.materialInput.value.quiltId === undefined
                        )
                    })
                    this.notifications.showInfo(`${testName}: PASS — quiltId is now undefined`)
                } catch {
                    this.notifications.showError(`${testName}: FAIL — quiltId did not become undefined`)
                }
            },
        },
        {
            type: "test",
            name: "Set Color",
            tooltip: "Sets a color on the selected piping/tape element. Click a piping or tape element first.",
            fct: async () => {
                const testName = "Set Color"
                const bdApi = this.getBdApi()
                const state = await this.getCurrentState(bdApi)
                const selected = state.selectedElement
                if (!selected || (selected.type !== "Piping" && selected.type !== "Tape")) {
                    this.notifications.showError(`${testName}: FAIL — no Piping or Tape selected`)
                    return
                }
                const inputId = selected.materialInput.id
                const color = "#ff6600"
                const substrateId = 35478
                bdApi.setColorWithSubstrate(inputId, color, substrateId)
                try {
                    await this.waitForState(bdApi, (s) => {
                        const el = s.selectedElement
                        if (!el || (el.type !== "Piping" && el.type !== "Tape")) return false
                        return (
                            !!el.materialInput.value &&
                            isColorWithSubstrate(el.materialInput.value) &&
                            el.materialInput.value.color === color &&
                            el.materialInput.value.colorSubstrateLegacyId === substrateId
                        )
                    })
                    this.notifications.showInfo(`${testName}: PASS — color=${color}, substrateId=${substrateId}`)
                } catch {
                    this.notifications.showError(`${testName}: FAIL — color did not become ${color} with substrate ${substrateId}`)
                }
            },
        },
        {
            type: "test",
            name: "Add Piping",
            tooltip: "Adds a Piping accessory to the first available seam. No element selection needed.",
            fct: async () => {
                const testName = "Add Piping"
                const bdApi = this.getBdApi()
                const state = await this.getCurrentState(bdApi)
                const firstSeam = state.accessoryGroupData[0]
                if (!firstSeam) {
                    this.notifications.showError(`${testName}: FAIL — no seams available`)
                    return
                }
                const countBefore = firstSeam.accessories.length
                const templateId = "c97f5318-cb62-46e4-9525-ffbfe8f2412c"
                await bdApi.addAccessory(firstSeam.seamId, templateId)
                try {
                    await this.waitForState(bdApi, (s) => {
                        const seam = s.accessoryGroupData.find((g) => g.seamId === firstSeam.seamId)
                        if (!seam) return false
                        return seam.accessories.length === countBefore + 1
                    })
                    this.notifications.showInfo(`${testName}: PASS — accessory count went from ${countBefore} to ${countBefore + 1}`)
                } catch {
                    this.notifications.showError(`${testName}: FAIL — accessory count did not increase`)
                }
            },
        },
        {
            type: "test",
            name: "Assign Template",
            tooltip: "Assigns a new template to the selected piping/tape element. Click a piping element first.",
            fct: async () => {
                const testName = "Assign Template"
                const bdApi = this.getBdApi()
                const state = await this.getCurrentState(bdApi)
                const selected = state.selectedElement
                if (!selected || (selected.type !== "Piping" && selected.type !== "Tape")) {
                    this.notifications.showError(`${testName}: FAIL — no Piping or Tape selected`)
                    return
                }
                const templateInputId = selected.templateInputId
                if (!templateInputId) {
                    this.notifications.showError(`${testName}: FAIL — selected element has no templateInputId`)
                    return
                }
                const templateId = "d4f44f04-4046-4cee-b798-f257f8e40b05"
                bdApi.assignTemplateToAccessory(templateInputId, templateId)
                try {
                    await this.waitForState(bdApi, (s) => {
                        const el = s.selectedElement
                        if (!el || (el.type !== "Piping" && el.type !== "Tape")) return false
                        return el.templateId === templateId
                    })
                    this.notifications.showInfo(`${testName}: PASS — templateId is now ${templateId}`)
                } catch {
                    this.notifications.showError(`${testName}: FAIL — templateId did not become ${templateId}`)
                }
            },
        },
        {
            type: "test",
            name: "Add Zipper",
            tooltip: "Adds a Zipper accessory to the first available seam. No element selection needed.",
            fct: async () => {
                const testName = "Add Zipper"
                const bdApi = this.getBdApi()
                const state = await this.getCurrentState(bdApi)
                const firstSeam = state.accessoryGroupData[0]
                if (!firstSeam) {
                    this.notifications.showError(`${testName}: FAIL — no seams available`)
                    return
                }
                const countBefore = firstSeam.accessories.length
                await bdApi.addAccessory(firstSeam.seamId, zipperTemplateId)
                try {
                    await this.waitForState(bdApi, (s) => {
                        const seam = s.accessoryGroupData.find((g) => g.seamId === firstSeam.seamId)
                        if (!seam) return false
                        return seam.accessories.length === countBefore + 1
                    })
                    this.notifications.showInfo(`${testName}: PASS — accessory count went from ${countBefore} to ${countBefore + 1}`)
                } catch {
                    this.notifications.showError(`${testName}: FAIL — accessory count did not increase`)
                }
            },
        },
        {
            type: "test",
            name: "Add Puller",
            tooltip: "Adds a puller to the selected zipper. Run 'Add Zipper' first, then click the zipper.",
            fct: async () => {
                const testName = "Add Puller"
                const bdApi = this.getBdApi()
                const state = await this.getCurrentState(bdApi)
                const selected = state.selectedElement
                if (!selected || selected.type !== "Zipper") {
                    this.notifications.showError(`${testName}: FAIL — no Zipper selected`)
                    return
                }
                if (selected.puller) {
                    this.notifications.showError(`${testName}: FAIL — zipper already has a puller. Run 'Remove Puller' first.`)
                    return
                }
                //A puller is not a seam accessory: it is a template slot inside the zipper, matching the puller size to the zipper size.
                bdApi.assignTemplateToAccessory(selected.puller0TemplateInputId, pullerTemplateId)
                try {
                    await this.waitForState(bdApi, (s) => {
                        const el = s.selectedElement
                        return !!el && el.type === "Zipper" && el.puller?.templateId === pullerTemplateId
                    })
                    this.notifications.showInfo(`${testName}: PASS — puller templateId is now ${pullerTemplateId}`)
                } catch {
                    this.notifications.showError(`${testName}: FAIL — puller was not added to the zipper`)
                }
            },
        },
        {
            type: "test",
            name: "Set Puller Position",
            tooltip: "Sets the position of the selected puller. Run 'Add Zipper' and 'Add Puller' first, then click the puller.",
            fct: async () => {
                const testName = "Set Puller Position"
                const bdApi = this.getBdApi()
                const state = await this.getCurrentState(bdApi)
                const selected = state.selectedElement
                if (!selected || selected.type !== "Puller") {
                    this.notifications.showError(`${testName}: FAIL — no Puller selected`)
                    return
                }
                const position = 0.98
                bdApi.setNumberInput(selected.positionInputId, position)
                try {
                    await this.waitForState(bdApi, (s) => {
                        const el = s.selectedElement
                        return !!el && el.type === "Puller" && el.position === position
                    })
                    this.notifications.showInfo(`${testName}: PASS — pullerPosition is now ${position}`)
                } catch {
                    this.notifications.showError(`${testName}: FAIL — pullerPosition did not become ${position}`)
                }
            },
        },
        {
            type: "test",
            name: "Remove Puller",
            tooltip: "Removes the puller from its zipper. Run 'Add Puller' first, then click the puller.",
            fct: async () => {
                const testName = "Remove Puller"
                const bdApi = this.getBdApi()
                const state = await this.getCurrentState(bdApi)
                const selected = state.selectedElement
                if (!selected || selected.type !== "Puller") {
                    this.notifications.showError(`${testName}: FAIL — no Puller selected`)
                    return
                }
                bdApi.removePuller(selected.templateInputId)
                try {
                    await this.waitForState(bdApi, (s) => s.selectedElement?.type !== "Puller")
                    this.notifications.showInfo(`${testName}: PASS — puller removed`)
                } catch {
                    this.notifications.showError(`${testName}: FAIL — puller was not removed`)
                }
            },
        },
        {
            type: "test",
            name: "Remove Accessory",
            tooltip: "Removes the first accessory of the first seam. Run 'Add Piping' or 'Add Zipper' first.",
            fct: async () => {
                const testName = "Remove Accessory"
                const bdApi = this.getBdApi()
                const state = await this.getCurrentState(bdApi)
                const firstSeam = state.accessoryGroupData[0]
                if (!firstSeam) {
                    this.notifications.showError(`${testName}: FAIL — no seams available`)
                    return
                }
                if (firstSeam.accessories.length === 0) {
                    this.notifications.showError(`${testName}: FAIL — no accessories to remove. Run 'Add Piping' first.`)
                    return
                }
                const countBefore = firstSeam.accessories.length
                bdApi.removeAccessory(firstSeam.seamId, 0)
                try {
                    await this.waitForState(bdApi, (s) => {
                        const seam = s.accessoryGroupData.find((g) => g.seamId === firstSeam.seamId)
                        if (!seam) return false
                        return seam.accessories.length === countBefore - 1
                    })
                    this.notifications.showInfo(`${testName}: PASS — accessory count went from ${countBefore} to ${countBefore - 1}`)
                } catch {
                    this.notifications.showError(`${testName}: FAIL — accessory was not removed`)
                }
            },
        },
        {
            type: "test",
            name: "Save Camera View",
            tooltip: "Reads the current camera. Afterwards orbit/zoom the model, then run 'Restore Camera View'.",
            fct: () => {
                const testName = "Save Camera View"
                this.savedCameraParams = this.getBdApi().getCurrentCameraParams()
                this.notifications.showInfo(`${testName}: camera saved`)
                this.logger.info(`${testName}`, {params: this.savedCameraParams})
            },
        },
        {
            type: "test",
            name: "Restore Camera View",
            tooltip: "Restores the camera saved by 'Save Camera View'. Run that first, then move the camera.",
            fct: () => {
                const testName = "Restore Camera View"
                const saved = this.savedCameraParams
                if (!saved) {
                    this.notifications.showError(`${testName}: nothing saved. Run 'Save Camera View' first.`)
                    return
                }
                this.getBdApi().setCamera(saved)
                this.notifications.showInfo(`${testName}: camera restored`)
            },
        },
        {
            type: "test",
            name: "Load Default Camera",
            tooltip: "Orbit the model away from its default framing first, then run this. The view should snap back to the standard mattress camera.",
            fct: async () => {
                const testName = "Load Default Camera"
                try {
                    const bdApi = this.getBdApi()
                    await bdApi.loadDefaultCamera()
                    this.notifications.showInfo(`${testName}: applied`)
                    this.logger.info(`${testName}`, {params: bdApi.getCurrentCameraParams()})
                } catch (error) {
                    this.notifications.showError(`${testName}: FAIL — ${error instanceof Error ? error.message : String(error)}`)
                }
            },
        },
        {
            type: "test",
            name: "Snapshot Without Selection",
            tooltip: "Select an element first, then run this. The downloaded snapshot must not show the selection outline.",
            fct: async () => {
                const testName = "Snapshot Without Selection"
                try {
                    const doc = this.$iframe().nativeElement.contentDocument
                    const configurator = doc?.querySelector("cm-configurator-main") as CMConfiguratorElement
                    if (!configurator) throw new Error("Configurator element not found")
                    const bdApi = this.getBdApi()
                    bdApi.clearSelection()
                    await bdApi.sync(true)
                    configurator.saveSnapshot("image/png")
                } catch (error) {
                    this.notifications.showError(`${testName}: FAIL — ${error instanceof Error ? error.message : String(error)}`)
                }
            },
        },
    ]

    ngAfterViewInit() {
        this.loadConfigurator()
    }

    constructor() {
        effect(() => {
            this.loadConfigurator()
        })
    }

    private async loadConfigurator() {
        const iframe = this.$iframe().nativeElement
        await updateIframe(iframe, getBdCloudPage(this.$moduleUrl(), this.bdCloudTemplateUuid))
    }

    private compareUsedElements(actual: UsedElements, expected: UsedElements): void {
        const errors: string[] = []

        // Compare assets
        const toKey = (e: {type: string; colormassId?: string; bdId?: string}) => `${e.type}:${e.colormassId}:${e.bdId}`
        const actualAssetKeys = new Set(actual.assets.map(toKey))
        const expectedAssetKeys = new Set(expected.assets.map(toKey))
        const missingAssets = expected.assets.filter((e) => !actualAssetKeys.has(toKey(e)))
        const unexpectedAssets = actual.assets.filter((a) => !expectedAssetKeys.has(toKey(a)))
        if (missingAssets.length > 0) errors.push(`${missingAssets.length} missing assets`)
        if (unexpectedAssets.length > 0) errors.push(`${unexpectedAssets.length} unexpected assets`)

        // Compare colors
        const actualColorSet = new Set(actual.colors)
        const expectedColorSet = new Set(expected.colors)
        const missingColors = expected.colors.filter((c) => !actualColorSet.has(c))
        const unexpectedColors = actual.colors.filter((c) => !expectedColorSet.has(c))
        if (missingColors.length > 0) errors.push(`${missingColors.length} missing colors`)
        if (unexpectedColors.length > 0) errors.push(`${unexpectedColors.length} unexpected colors`)

        if (errors.length === 0) {
            this.notifications.showInfo("getUsedElements: PASS")
        } else {
            this.notifications.showError(`getUsedElements: FAIL — ${errors.join(", ")}`)
            this.logger.error("getUsedElements: FAIL", {missingAssets, unexpectedAssets, missingColors, unexpectedColors, actual})
        }
    }

    private getCurrentDimensions(bdApi: BdApi): Promise<NumberValue[]> {
        return new Promise((resolve, reject) => {
            const unregister = bdApi.registerStateChangeCallback((state: ConfiguratorState) => {
                unregister()
                if (state.dimensions) {
                    resolve(state.dimensions)
                } else {
                    reject(new Error("No dimensions in configurator state"))
                }
            })
        })
    }

    private getCurrentState(bdApi: BdApi): Promise<ConfiguratorState> {
        return new Promise((resolve) => {
            const unregister = bdApi.registerStateChangeCallback((state: ConfiguratorState) => {
                unregister()
                resolve(state)
            })
        })
    }

    private waitForState(bdApi: BdApi, predicate: (state: ConfiguratorState) => boolean, timeoutMs = 5000): Promise<ConfiguratorState> {
        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                unregister()
                reject(new Error("Timed out waiting for expected state"))
            }, timeoutMs)
            const unregister = bdApi.registerStateChangeCallback((state: ConfiguratorState) => {
                if (predicate(state)) {
                    clearTimeout(timeout)
                    unregister()
                    resolve(state)
                }
            })
        })
    }

    private getBdApi(): BdApi {
        const doc = this.$iframe().nativeElement.contentDocument
        if (!doc) throw new Error("No content document available")
        const apiElement = doc.querySelector("cm-bdcloud-api") as BdApiWebcomponent | null
        if (!apiElement?.bdApi) throw new Error("BdApi not found")
        return apiElement.bdApi
    }
}
